import React, { useState } from 'react';
import { Modal, Form, InputNumber, DatePicker, Button, Space, message, Tag } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import axios from 'axios';

const CloneInvoiceDialog = ({ visible, onClose, invoice, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Watch form values for reactive preview updates
  const cloneCount = Form.useWatch('clone_count', form);
  const dateIncrementDays = Form.useWatch('date_increment_days', form);

  // Quick preset configurations
  const presets = [
    { label: '12 Monthly', count: 12, days: 30, color: 'blue' },
    { label: '4 Quarterly', count: 4, days: 91, color: 'green' },
    { label: '52 Weekly', count: 52, days: 7, color: 'orange' },
    { label: '6 Bi-Monthly', count: 6, days: 60, color: 'purple' },
  ];

  const handlePresetClick = (preset) => {
    form.setFieldsValue({
      clone_count: preset.count,
      date_increment_days: preset.days,
    });
  };

  const handleClone = async (values) => {
    setLoading(true);
    try {
      const payload = {
        clone_count: values.clone_count,
        date_increment_days: values.date_increment_days || 0,
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : undefined,
      };

      const response = await axios.post(
        `/invoices/invoices/${invoice.id}/clone/`,
        payload
      );

      message.success(response.data.message || `Successfully created ${values.clone_count} invoice clone(s)`);

      if (onSuccess) {
        onSuccess(response.data.cloned_invoices);
      }

      form.resetFields();
      onClose();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to clone invoice';
      message.error(errorMessage);
      console.error('Clone error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <CopyOutlined />
          <span>Clone Invoice</span>
          {invoice && <Tag color="blue">{invoice.inv_number}</Tag>}
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleClone}
        initialValues={{
          clone_count: 1,
          date_increment_days: 30,
        }}
      >
        {/* Quick Presets */}
        <Form.Item label="Quick Presets">
          <Space wrap>
            {presets.map((preset) => (
              <Tag
                key={preset.label}
                color={preset.color}
                style={{ cursor: 'pointer', padding: '4px 12px', fontSize: '13px' }}
                onClick={() => handlePresetClick(preset)}
              >
                {preset.label}
              </Tag>
            ))}
          </Space>
        </Form.Item>

        {/* Clone Count */}
        <Form.Item
          label="Number of Clones"
          name="clone_count"
          rules={[
            { required: true, message: 'Please enter number of clones' },
            { type: 'number', min: 1, max: 100, message: 'Must be between 1 and 100' },
          ]}
          extra="How many invoice copies to create (1-100)"
        >
          <InputNumber
            min={1}
            max={100}
            style={{ width: '100%' }}
            placeholder="Enter number of clones"
          />
        </Form.Item>

        {/* Date Increment */}
        <Form.Item
          label="Days Between Each Clone"
          name="date_increment_days"
          extra="Set to 0 for same date on all clones. Use 30 for monthly, 7 for weekly, 91 for quarterly."
        >
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            placeholder="Enter days increment (e.g., 30 for monthly)"
            addonAfter="days"
          />
        </Form.Item>

        {/* Starting Date */}
        <Form.Item
          label="Starting Date (Optional)"
          name="start_date"
          extra="Leave empty to use the original invoice date"
        >
          <DatePicker
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
            placeholder="Select starting date"
          />
        </Form.Item>

        {/* Preview Info */}
        <Form.Item>
          <div
            style={{
              background: '#f0f2f5',
              padding: '12px',
              borderRadius: '4px',
              fontSize: '13px',
            }}
          >
            <strong>Preview:</strong>
            <div style={{ marginTop: '8px' }}>
              {cloneCount && (
                <div>
                  ✓ Will create <strong>{cloneCount}</strong> invoice{cloneCount > 1 ? 's' : ''}
                </div>
              )}
              {dateIncrementDays > 0 && (
                <div>
                  ✓ Each invoice will be <strong>{dateIncrementDays} days</strong> apart
                </div>
              )}
              {dateIncrementDays === 0 && (
                <div>
                  ✓ All invoices will have the <strong>same date</strong>
                </div>
              )}
            </div>
          </div>
        </Form.Item>

        {/* Action Buttons */}
        <Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading} icon={<CopyOutlined />}>
              Create {cloneCount || 1} Clone{(cloneCount || 1) > 1 ? 's' : ''}
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* Help Text */}
      <div style={{ marginTop: '16px', padding: '12px', background: '#e6f7ff', borderRadius: '4px', fontSize: '12px' }}>
        <strong>💡 Common Use Cases:</strong>
        <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
          <li><strong>Monthly recurring:</strong> 12 clones, 30 days apart</li>
          <li><strong>Quarterly billing:</strong> 4 clones, 91 days apart</li>
          <li><strong>Weekly invoices:</strong> 52 clones, 7 days apart</li>
          <li><strong>Same invoice, different dates:</strong> Use presets or custom settings</li>
        </ul>
      </div>
    </Modal>
  );
};

export default CloneInvoiceDialog;
